import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { AdminOssRequest } from '../lib/adminOss';
import { OSS_STATUS_LABEL, OSS_STATUS_TONE } from '../lib/ossVerification';
import { absolute } from '../lib/time';
import { AdminOssDecision } from './AdminOssDecision';
import { DataTable, type DataTableColumn } from './DataTable';
import { StatusPill } from './StatusPill';

// The OSS-verification queue as a dense data table (spec §3.15). Presenter —
// the panel owns the accumulated rows, the filter, and pagination; this paints
// one row per application, and `<AdminOssDecision/>` owns each row's verdict.
export function AdminOssTable(props: {
  rows: readonly AdminOssRequest[];
  token: string | null;
  onDecided: () => void;
}): JSX.Element {
  const columns: readonly DataTableColumn<AdminOssRequest>[] = [
    {
      key: 'repo',
      rowHeader: true,
      header: t('admin.oss_col_repo'),
      cell: (row) => (
        <div class="admin-row__stack">
          <span class="data-table__mono">{row.repo_full_name}</span>
          <span class="admin-row__meta">{t('admin.oss_from', { login: row.account_login })}</span>
          <Show when={row.submitted_by_login}>
            {(login) => (
              <span class="admin-row__meta">{t('admin.oss_submitted_by', { login: login() })}</span>
            )}
          </Show>
        </div>
      ),
    },
    {
      key: 'evidence',
      header: t('admin.oss_col_evidence'),
      // The evidence scrolls, so it is a named tab stop (`<section aria-label>`,
      // the `<ScrollPort/>` spelling) — a keyboard reader can scroll the rest.
      cell: (row) => (
        <section
          class="admin-row__text"
          tabindex="0"
          aria-label={t('admin.oss_evidence_label', { repo: row.repo_full_name })}
        >
          {row.evidence}
        </section>
      ),
    },
    {
      key: 'status',
      header: t('admin.oss_col_status'),
      cell: (row) => (
        <div class="admin-row__stack">
          <StatusPill tone={OSS_STATUS_TONE[row.status]}>
            {OSS_STATUS_LABEL[row.status]()}
          </StatusPill>
          <p class="admin-row__meta">{absolute(row.submitted_at_iso)}</p>
        </div>
      ),
    },
    {
      key: 'actions',
      header: t('admin.col_actions'),
      align: 'end',
      cell: (row) => <AdminOssDecision row={row} token={props.token} onDecided={props.onDecided} />,
    },
  ];

  return (
    <DataTable
      label={t('admin.oss_table_label')}
      columns={columns}
      rows={props.rows}
      rowKey={(row) => row.id}
    />
  );
}
